/**
 * Shared Add/Edit Product Form Body
 * Extracted from BeverageStockMasterPage — used by both the Product Master
 * management page and the PSB Stage 1B "Add to Product Master" modal so that
 * the same category cascade, code allocator, brand field, and full UnitRow
 * table are used everywhere with zero duplication.
 *
 * Props
 * ──────
 *   form             — form state { product_name, product_code, brand,
 *                        category_id, category, subcategory_id, subcategory,
 *                        units: [...] }
 *   setForm          — state setter (same shape as BeverageStockMasterPage)
 *   categoryOptions  — [{ id, slug, label, subcategories: [{id,name,code_min,code_max}] }]
 *   dynamicUnitTypes — ['bottle','keg',…]
 *   dynamicUnitLabels— { bottle:'Bottle', … }
 *   dynamicUnitMlMap — { bottle:700, … }
 *   showCost         — bool (default false)
 *   showBarcode      — bool (default false)
 *   showStatus       — bool (default false)
 *   namePrefillBadge — optional ReactNode rendered after the product-name label
 */
import { getBackendUrl } from '@/lib/backendUrl';
import React, { useMemo } from 'react';
import axios from 'axios';
import { Label }  from '@/components/ui/label';
import { Input }  from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { KNOWN_BASES, measureTypeOf } from './measureType';
import { SnackNetGramsPanel } from './SnackNetGramsPanel';
import { PortionYieldPanel } from './PortionYieldPanel';
import { PacketPatternHelper } from './PacketPatternHelper';
import { CaseHelper, syncCountCase, isCountCaseRow } from './CaseHelper';

const API = getBackendUrl();

// ── Default measure lists (same as BeverageStockMasterPage) ─────────────────
export const DEFAULT_UNIT_TYPES  = ['bottle','keg','case','litre','ml','each','can','pint','half_pint','glass','shot','gallon','optic'];
export const DEFAULT_UNIT_LABELS = { bottle:'Bottle',keg:'Keg',case:'Case',litre:'Litre',ml:'ml',each:'Each',can:'Can',pint:'Pint',half_pint:'Half Pint',glass:'Glass',shot:'Shot',gallon:'Gallon',optic:'Optic' };

// ── emptyUnit / emptyProduct (same shape as BeverageStockMasterPage) ─────────
export const emptyUnit = () => ({
  unit_type:'', base_unit:'ml', conversion_to_base:'',
  is_default_count_unit:false, is_default_purchase_unit:false,
  is_sales_unit:false, is_default_sales_unit:false,
  waste_factor:'', observed_servings_per_unit:'',
  net_grams:'', net_grams_evidence:'',
});

export const emptyProductBase = () => ({
  product_name:'', product_code:'', brand:'',
  category:'', category_id:null,
  subcategory:'', subcategory_id:null,
  internal_note:'',
  units:[{ ...emptyUnit(), is_default_count_unit:true }],
});

// Base select — known bases plus any existing stored value (left as-is).
function BaseSelect({ value, onChange, index }) {
  const v = value || 'ml';
  const options = KNOWN_BASES.includes(v) ? KNOWN_BASES : [...KNOWN_BASES, v];
  return (
    <Select value={v} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs w-[72px]" data-testid={`unit-base-${index}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map(b => (
          <SelectItem key={b} value={b}>{b}{measureTypeOf(b) === 'unknown' ? ' (?)' : ''}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── UnitRow — identical to BeverageStockMasterPage ───────────────────────────
export function UnitRow({ unit, index, onChange, onRemove, isOnly, unitTypes, unitLabels, unitMlMap }) {
  const update = (field, val) => onChange(index, { ...unit, [field]: val });
  const isCustom   = unit.unit_type && !unitTypes.includes(unit.unit_type);
  const selectValue = isCustom ? 'custom' : (unit.unit_type || 'custom');
  return (
    <TableRow data-testid={`unit-row-${index}`}>
      <TableCell className="py-1.5">
        <Select value={selectValue} onValueChange={(v) => {
          if (v === 'custom') {
            update('unit_type', unit._customDraft || '');
          } else {
            // Measure-default ml fill applies to volume rows only (beverage
            // default unchanged); never overwrites a g / each row's size.
            const ml = unitMlMap && measureTypeOf(unit.base_unit || 'ml') === 'volume' ? unitMlMap[v] : null;
            if (ml) onChange(index, { ...unit, unit_type: v, conversion_to_base: String(ml) });
            else update('unit_type', v);
          }
        }}>
          <SelectTrigger className="h-8 text-xs" data-testid={`unit-type-${index}`}><SelectValue placeholder="Type…" /></SelectTrigger>
          <SelectContent>
            {unitTypes.map(t => (
              <SelectItem key={t} value={t}>{unitLabels[t] || (t.charAt(0).toUpperCase() + t.slice(1))}</SelectItem>
            ))}
            <SelectItem value="custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
        {(selectValue === 'custom' || isCustom) && (
          <Input className="h-8 text-xs mt-1" placeholder="e.g. measure, tot, nip"
            value={unit.unit_type}
            onChange={(e) => update('unit_type', e.target.value)}
            data-testid={`unit-custom-${index}`} />
        )}
        {/* AI unresolved unit-type hint — shown when AI suggested a measure not in catalogue.
            Regression case: Serenello 0% 125ml serving returned 'can_125ml' by AI — was
            silently written as unit_type; now left blank with this visible warning. */}
        {!unit.unit_type && unit._ai_unit_label && (
          <p className="text-[10px] text-amber-600 mt-0.5 leading-tight" data-testid={`unit-ai-hint-${index}`}>
            {unit.conversion_to_base ? `Serving measure ${unit.conversion_to_base}ml` : 'Serving measure'} — select unit type above
          </p>
        )}
      </TableCell>
      <TableCell className="py-1.5">
        <div className="flex items-center gap-1">
          <Input type="number" className="h-8 text-xs w-24"
            value={unit.conversion_to_base}
            onChange={(e) => update('conversion_to_base', e.target.value)}
            placeholder={measureTypeOf(unit.base_unit) === 'count' ? 'e.g. 1' : 'e.g. 700'}
            data-testid={`unit-conversion-${index}`} />
          <span className="text-[10px] text-slate-500 w-7" data-testid={`unit-size-suffix-${index}`}>{unit.base_unit || 'ml'}</span>
        </div>
      </TableCell>
      <TableCell className="py-1.5">
        <BaseSelect value={unit.base_unit} index={index} onChange={(v) => update('base_unit', v)} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Switch checked={unit.is_default_count_unit}
          onCheckedChange={(v) => onChange(index, { ...unit, is_default_count_unit: v }, 'default_count')}
          data-testid={`unit-default-count-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Switch checked={unit.is_default_purchase_unit}
          onCheckedChange={(v) => onChange(index, { ...unit, is_default_purchase_unit: v }, 'default_purchase')}
          data-testid={`unit-default-purchase-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Switch checked={!!unit.is_sales_unit}
          onCheckedChange={(v) => onChange(index, { ...unit, is_sales_unit: v }, 'sales')}
          data-testid={`unit-sales-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Switch checked={!!unit.is_default_sales_unit}
          onCheckedChange={(v) => onChange(index, { ...unit, is_default_sales_unit: v, is_sales_unit: v || unit.is_sales_unit }, 'default_sales')}
          data-testid={`unit-default-sales-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Input type="number" step="0.01" min="0"
          value={unit.observed_servings_per_unit || ''}
          onChange={(e) => update('observed_servings_per_unit', e.target.value)}
          disabled={!unit.is_sales_unit}
          placeholder={unit.is_sales_unit ? 'nominal' : '—'}
          className="h-8 text-xs w-16 text-right tabular-nums"
          data-testid={`unit-observed-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Input type="number" step="0.01" min="0" max="100"
          value={unit.waste_factor || ''}
          onChange={(e) => update('waste_factor', e.target.value)}
          disabled={!unit.is_sales_unit}
          placeholder={unit.is_sales_unit ? '0' : '—'}
          className="h-8 text-xs w-14 text-right tabular-nums"
          data-testid={`unit-waste-${index}`} />
      </TableCell>
      <TableCell className="py-1.5 text-center">
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={isOnly}
          type="button"
          onClick={() => onRemove(index)}
          data-testid={`unit-remove-${index}`}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ── handleUnitChange helper (enforces single-default invariants) ─────────────
export function handleUnitChange(setForm, index, updated, flag) {
  setForm(f => {
    let units = f.units.map((u, i) => (i === index ? updated : u));
    if (flag === 'default_count' && updated.is_default_count_unit)
      units = units.map((u, i) => i === index ? u : { ...u, is_default_count_unit: false });
    if (flag === 'default_purchase' && updated.is_default_purchase_unit)
      units = units.map((u, i) => i === index ? u : { ...u, is_default_purchase_unit: false });
    if (flag === 'default_sales' && updated.is_default_sales_unit)
      units = units.map((u, i) => i === index ? u : { ...u, is_default_sales_unit: false });
    return { ...f, units };
  });
}

// ── AddProductFormBody ───────────────────────────────────────────────────────
export function AddProductFormBody({
  form,
  setForm,
  categoryOptions = [],
  dynamicUnitTypes  = DEFAULT_UNIT_TYPES,
  dynamicUnitLabels = DEFAULT_UNIT_LABELS,
  dynamicUnitMlMap  = {},
  showCost          = false,
  showBarcode       = false,
  showStatus        = false,
  showCaseSize      = false,
  namePrefillBadge  = null,
  // Optional: pass the parent's handleUnitChange to override the default
  // (BeverageStockMasterPage passes its own version for yield auto-stub behaviour)
  onUnitChange      = null,
  // Stage 1A
  isCreate          = false,
  showInternalNote  = true,
  duplicateReview   = null,
}) {
  // Derive the category row that matches the current form value.
  // The legacy `category` enum is stored uppercase ('SPIRITS') but taxonomy slugs
  // are lowercase ('spirits') — always compare case-insensitively so the form
  // resolves correctly even when the taxonomy loads after the dialog opens.
  //
  // iter-newport-fix: When category_id is set but not found in categoryOptions
  // (taxonomy still loading → fallback list has id:null for every entry), fall
  // through to the legacy slug match so Category never shows "None" on edit open.
  const formCategoryOption = useMemo(() => {
    if (form.category_id) {
      const byId = categoryOptions.find(c => c.id === form.category_id);
      if (byId) return byId;
      // ID not found — taxonomy may still be loading; fall through to slug match.
    }
    if (form.category) {
      const lc = form.category.toLowerCase();
      return categoryOptions.find(c => (c.slug || '').toLowerCase() === lc) || null;
    }
    return null;
  }, [categoryOptions, form.category_id, form.category]);

  // Subcategory options: prefer real taxonomy subcategories for the resolved
  // category.  When taxonomy is in fallback mode (subcategories=[]) but the
  // product already has a subcategory assigned, synthesise a single display
  // option from form state so the existing choice is visible and the select is
  // not falsely shown as empty.  This does NOT change the saved value.
  const subcategoryOptions = useMemo(() => {
    const real = formCategoryOption?.subcategories || [];
    if (real.length > 0) return real;
    if (form.subcategory_id) {
      return [{ id: form.subcategory_id, name: form.subcategory || String(form.subcategory_id) }];
    }
    return [];
  }, [formCategoryOption, form.subcategory_id, form.subcategory]);

  // "Suggest next code" — same logic as BeverageStockMasterPage
  const subRow = formCategoryOption?.subcategories?.find(s => s.id === form.subcategory_id) || null;
  const hasRange = !!(subRow && subRow.code_min != null && subRow.code_max != null);

  const handleSuggestCode = async () => {
    if (!form.subcategory_id) return;
    try {
      const r = await axios.get(
        `${API}/api/product-taxonomy/subcategories/${form.subcategory_id}/next-product-code`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      if (r.data?.product_code) {
        setForm(f => ({ ...f, product_code: r.data.product_code }));
      }
    } catch (_) { /* silent — advisory */ }
  };

  const onUnitChangeInternal = (index, updated, flag) => {
    const prevConv = (form.units || [])[index]?.conversion_to_base;
    if (onUnitChange) {
      onUnitChange(index, updated, flag);
    } else {
      handleUnitChange(setForm, index, updated, flag);
    }
    // Count pattern: a direct edit of the case row's size keeps items-per-case and case_size equal
    if (isCountCaseRow(updated) && updated.conversion_to_base !== prevConv) setForm(f => syncCountCase(f));
  };

  return (
    <div className="space-y-5">
      {/* ── Product Name ── */}
      <div className="space-y-1.5">
        <Label htmlFor="apf-product-name">
          Product Name <span className="text-destructive">*</span>
          {namePrefillBadge && <span className="ml-1.5">{namePrefillBadge}</span>}
        </Label>
        <Input
          id="apf-product-name"
          value={form.product_name || ''}
          onChange={(e) => setForm(f => ({ ...f, product_name: e.target.value }))}
          placeholder="e.g. Smirnoff Vodka 70cl"
          data-testid="apf-product-name"
          required
        />
      </div>

      {/* ── Product Code — system-assigned (read-only) ── */}
      {/* Backend auto-allocates via _allocate_product_code(); any value
          typed here is silently ignored. Field is read-only to prevent
          false expectation of custom codes (PSB 422 gap fix). */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="apf-product-code" className="flex items-center gap-1.5">
            Product Code
            <span
              className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200"
              title="The system assigns the next available code in the subcategory range automatically when the product is saved."
            >
              System-assigned
            </span>
          </Label>
          {form.subcategory_id && !hasRange && (
            <a
              href={`/stocktaking/product-categories?focus_sub=${form.subcategory_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-amber-700 hover:text-amber-800 underline"
              data-testid="apf-configure-code-range-link"
            >
              Configure code range →
            </a>
          )}
        </div>
        <Input
          id="apf-product-code"
          value={form.product_code || ''}
          readOnly
          disabled
          placeholder={
            form.subcategory_id
              ? 'Auto-assigned on save'
              : 'Select a subcategory first'
          }
          className="bg-slate-50 text-slate-500 cursor-not-allowed"
          data-testid="apf-product-code"
        />
        <p className="text-[11px] text-slate-400 leading-tight">
          The next available code in the subcategory range is assigned automatically when you save.
        </p>
      </div>

      {/* ── Brand ── */}
      <div className="space-y-1.5">
        <Label htmlFor="apf-brand">Brand</Label>
        <Input
          id="apf-brand"
          value={form.brand || ''}
          onChange={(e) => setForm(f => ({ ...f, brand: e.target.value }))}
          placeholder="e.g. Smirnoff"
          data-testid="apf-brand"
        />
      </div>

      {/* ── Category cascade ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select
            value={
              // Drive the Select from the resolved formCategoryOption so the
              // displayed value always matches an item in the list, whether the
              // taxonomy uses new FK ids or the legacy-enum slug fallback.
              formCategoryOption
                ? (formCategoryOption.id ? String(formCategoryOption.id) : formCategoryOption.slug)
                : 'none'
            }
            onValueChange={(v) => {
              if (v === 'none') {
                setForm(f => ({ ...f, category:'', category_id:null, subcategory:'', subcategory_id:null }));
                return;
              }
              const opt = categoryOptions.find(c => (c.id && String(c.id) === v) || (c.slug || '').toLowerCase() === v.toLowerCase());
              if (!opt) return;
              setForm(f => {
                const categoryChanging = f.category_id !== opt.id;
                return {
                  ...f,
                  category_id: opt.id,
                  category: ['beer','wine','spirits','soft_drinks','cider','mixers','other'].includes(opt.slug) ? opt.slug : 'other',
                  subcategory:    categoryChanging ? '' : f.subcategory,
                  subcategory_id: categoryChanging ? null : f.subcategory_id,
                };
              });
            }}
          >
            <SelectTrigger data-testid="apf-select-category"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {categoryOptions.map(c => (
                <SelectItem key={c.id ?? c.slug} value={c.id ? String(c.id) : c.slug}>
                  {c.label}{!c.is_system && c.id ? ' (custom)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="apf-subcategory">Subcategory <span className="text-destructive">*</span></Label>
          {/* key forces Radix Select to remount when category changes — without
              this, subcategory options registered via useLayoutEffect in the old
              category remain cached and the new category's options never appear. */}
          <Select
            key={`subcat-${formCategoryOption?.id ?? 'none'}`}
            value={form.subcategory_id ? String(form.subcategory_id) : (form.subcategory || 'none')}
            onValueChange={(v) => {
              // Guard: Radix BubbleSelect can fire with v="" when the native <select>
              // hasn't yet had its options registered via useLayoutEffect (SelectItem
              // NativeOption registration is async to the render). Treat "" as a no-op
              // — the controlled `value` prop already reflects the correct state.
              if (!v) return;
              if (v === 'none') {
                setForm(f => ({ ...f, subcategory:'', subcategory_id:null })); return;
              }
              const subs = formCategoryOption?.subcategories || [];
              const match = subs.find(s => String(s.id) === v);
              if (match) setForm(f => ({ ...f, subcategory_id: match.id, subcategory: match.name, _aiSuggestedSubcategory: null }));
              else setForm(f => ({ ...f, subcategory_id: null, subcategory: v, _aiSuggestedSubcategory: null }));
            }}
            disabled={!formCategoryOption && !form.subcategory_id && !form.subcategory}
          >
            <SelectTrigger data-testid="apf-input-subcategory">
              <SelectValue placeholder={formCategoryOption ? 'Select subcategory…' : 'Pick a category first'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {subcategoryOptions.map(s => (
                <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* AI unresolved subcategory hint — shown when AI named a subcategory not in catalogue */}
          {!form.subcategory_id && form._aiSuggestedSubcategory && (
            <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5" data-testid="ai-subcategory-hint">
              <span className="font-semibold">AI suggested:</span> &ldquo;{form._aiSuggestedSubcategory}&rdquo; — not found in catalogue; select manually or leave unset
            </p>
          )}
        </div>
      </div>

      {/* Stage 1A — duplicate review (rendered by the parent, create only) */}
      {duplicateReview}

      {/* Stage 1A — internal note: editors only, never on reports or exports */}
      {showInternalNote && (
        <div className="space-y-1.5">
          <Label htmlFor="apf-internal-note">Internal note</Label>
          <textarea id="apf-internal-note" rows={2} maxLength={500}
            value={form.internal_note || ''}
            onChange={(e) => setForm(f => ({ ...f, internal_note: e.target.value }))}
            placeholder="e.g. verify case size with purchasing"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
            data-testid="apf-internal-note" />
          <p className="text-[11px] text-slate-400 leading-tight">Visible to product editors only. Never shown on reports or exports.</p>
        </div>
      )}

      {showCaseSize && (
        <div className="space-y-1.5 max-w-[200px]">
          <Label htmlFor="apf-case-size">Case / Outer Size</Label>
          <Input id="apf-case-size" type="number" min="1"
            value={form.case_size || ''}
            onChange={(e) => { const v = e.target.value; setForm(f => syncCountCase({ ...f, case_size: v }, { caseSize: v })); }}
            placeholder="e.g. 6, 12, 24" data-testid="apf-case-size" />
        </div>
      )}

      {/* Optional core fields — collapsed under Advanced; opens when it holds values */}
      {(showCost || showBarcode || showStatus) && (
        <details className="group rounded-md border border-slate-200 px-3 py-2"
          open={!!(form.standard_cost || form.barcode || (form.status && form.status !== 'active')) || undefined}
          data-testid="apf-advanced">
          <summary className="text-xs font-semibold text-slate-600 cursor-pointer flex items-center gap-1.5 select-none">
            <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" /> Advanced — reference cost, barcode, status
          </summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
          {showCost && (
            <div className="space-y-1.5">
              <Label htmlFor="apf-standard-cost">Reference Cost</Label>
              <Input id="apf-standard-cost" type="number" step="0.01"
                value={form.standard_cost || ''}
                onChange={(e) => setForm(f => ({ ...f, standard_cost: e.target.value }))}
                placeholder="0.00" data-testid="apf-standard-cost" />
              <p className="text-xs text-muted-foreground">Fallback only — actual costs come from deliveries</p>
            </div>
          )}
          {showBarcode && (
            <div className="space-y-1.5">
              <Label htmlFor="apf-barcode">Barcode</Label>
              <Input id="apf-barcode"
                value={form.barcode || ''}
                onChange={(e) => setForm(f => ({ ...f, barcode: e.target.value }))}
                placeholder="EAN / UPC" data-testid="apf-barcode" />
            </div>
          )}
          {showStatus && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status || 'active'} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="apf-select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        </details>
      )}

      {/* Task 6 (Wave 1) case helper — Stage 1A: labels follow the case row's base */}
      <CaseHelper form={form} setForm={setForm} />

      <Separator />

      {/* ── Units of Measure — identical table to BeverageStockMasterPage ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Units of Measure</Label>
          <Button variant="outline" size="sm"
            type="button"
            onClick={() => setForm(f => ({ ...f, units: [...f.units, emptyUnit()] }))}
            data-testid="apf-add-unit-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Unit
          </Button>
        </div>
        {isCreate && <PacketPatternHelper setForm={setForm} />}
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 text-xs">
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs" title="Size in the row's own base (ml, g or each)">Size</TableHead>
                <TableHead className="text-xs">Base</TableHead>
                <TableHead className="text-xs text-center" title="Count unit (one per product)">Count</TableHead>
                <TableHead className="text-xs text-center" title="Purchase unit (one per product)">Purchase</TableHead>
                <TableHead className="text-xs text-center" title="Sales unit (multiple allowed)">Sales</TableHead>
                <TableHead className="text-xs text-center" title="Default sales measure (one per product)">Dflt Sales</TableHead>
                <TableHead className="text-xs text-center" title="Observed servings per unit">Obs/Unit</TableHead>
                <TableHead className="text-xs text-center" title="Waste %">Waste%</TableHead>
                <TableHead className="text-xs text-center w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(form.units || []).map((u, i) => (
                <UnitRow key={i} unit={u} index={i}
                  isOnly={(form.units || []).length === 1}
                  unitTypes={dynamicUnitTypes}
                  unitLabels={dynamicUnitLabels}
                  unitMlMap={dynamicUnitMlMap}
                  onChange={onUnitChangeInternal}
                  onRemove={(idx) => setForm(f => ({ ...f, units: f.units.filter((_, j) => j !== idx) }))} />
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground">
          <strong>Count</strong> — unit auditors count in (one per product).{' '}
          <strong>Purchase</strong> — unit deliveries land in; drives post-mix yield (one per product).{' '}
          <strong>Sales</strong> — unit sold on the till (multiple allowed).{' '}
          <strong>Dflt Sales</strong> — default serving measure for the Wizard (one per product).
        </p>
      </div>

      <PortionYieldPanel form={form} setForm={setForm} />
      <SnackNetGramsPanel form={form} setForm={setForm} />
    </div>
  );
}
